import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  IconArrowLeft,
  IconFlame,
  IconAlertTriangle,
  IconShield,
  IconTrash,
  IconDownload,
} from '@tabler/icons-react';

export default function CoshhDetail() {
  const { coshhId } = useParams();
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const [substance, setSubstance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !coshhId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'coshh', coshhId));
        if (snap.exists()) setSubstance({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, coshhId]);

  const riskColour = (r) => {
    if (r === 'high') return 'var(--color-danger)';
    if (r === 'medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!substance) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/coshh')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> COSHH register
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Substance not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/coshh')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> COSHH register
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>{substance.productName}</h1>
              <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: riskColour(substance.riskLevel) + '18', color: riskColour(substance.riskLevel), textTransform: 'capitalize' }}>
                {substance.riskLevel} risk
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {substance.manufacturer || 'Unknown manufacturer'}{substance.siteName ? ' · ' + substance.siteName : ''}
            </div>
          </div>
          <Button variant="secondary" size="sm" icon={<IconDownload size={13} stroke={1.5} />} onClick={() => {}}>
            View datasheet
          </Button>
        </div>

        {substance.riskLevel === 'high' && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              High risk substance — ensure all operators have read the safety data sheet before use
            </div>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Substance details</div>
          {[
            { label: 'Product name',  value: substance.productName              },
            { label: 'Manufacturer',  value: substance.manufacturer || '—'      },
            { label: 'Risk level',    value: substance.riskLevel,  capitalise: true },
            { label: 'Site',          value: substance.siteName || 'All sites'  },
            { label: 'Last reviewed', value: substance.lastReviewDate || '—'    },
            { label: 'Next review',   value: substance.nextReviewDate || '—'    },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
              <span style={{ fontSize: 13, color: row.label === 'Risk level' ? riskColour(substance.riskLevel) : 'var(--color-text-primary)', textTransform: row.capitalise ? 'capitalize' : 'none' }}>
                {row.value}
              </span>
            </div>
          ))}
        </Card>

        {substance.hazardType?.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IconFlame size={16} color="var(--color-danger)" stroke={1.5} />
              <div style={{ fontSize: 14, fontWeight: 400 }}>Hazard types</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {substance.hazardType.map(h => (
                <span key={h} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'var(--color-danger-light)', color: 'var(--color-danger)', border: '0.5px solid var(--color-danger)' }}>
                  {h}
                </span>
              ))}
            </div>
          </Card>
        )}

        {substance.ppe?.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IconShield size={16} color="var(--color-brand)" stroke={1.5} />
              <div style={{ fontSize: 14, fontWeight: 400 }}>Required PPE</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {substance.ppe.map(p => (
                <span key={p} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'var(--color-brand-light)', color: 'var(--color-brand)', border: '0.5px solid var(--color-brand)' }}>
                  {p}
                </span>
              ))}
            </div>
          </Card>
        )}

        {substance.storageRequirements && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Storage requirements</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              {substance.storageRequirements}
            </div>
          </Card>
        )}

        {substance.disposalMethod && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <IconTrash size={16} color="var(--color-text-tertiary)" stroke={1.5} />
              <div style={{ fontSize: 14, fontWeight: 400 }}>Disposal method</div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              {substance.disposalMethod}
            </div>
          </Card>
        )}

      </div>
    </Layout>
  );
}